import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import {
  LayoutDashboard,
  ShoppingBag,
  UtensilsCrossed,
  Users,
  Menu,
  X,
  Bell,
  LogOut,
  Settings,
  Bot,
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/orders', label: 'Orders', icon: ShoppingBag },
  { path: '/menu', label: 'Menu', icon: UtensilsCrossed },
  { path: '/customers', label: 'Customers', icon: Users },
  { path: '/bot', label: 'Bot Settings', icon: Bot },
  { path: '/settings', label: 'Settings', icon: Settings },
];

// Bell shake animation style
const bellShakeStyle = `
  @keyframes bellShake {
    0% { transform: rotate(0deg); }
    10% { transform: rotate(15deg); }
    20% { transform: rotate(-15deg); }
    30% { transform: rotate(15deg); }
    40% { transform: rotate(-15deg); }
    50% { transform: rotate(10deg); }
    60% { transform: rotate(-10deg); }
    70% { transform: rotate(5deg); }
    80% { transform: rotate(-5deg); }
    90% { transform: rotate(2deg); }
    100% { transform: rotate(0deg); }
  }
  .bell-shake {
    animation: bellShake 0.8s ease-in-out;
  }
`;

export default function MainLayout({ user, themeColor }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellShaking, setBellShaking] = useState(false);
  const audioUnlocked = useRef(false);  

  const userInitial = user?.email?.[0]?.toUpperCase() || 'R';

  // Unlock audio on first click
  useEffect(() => {
    const unlock = () => {
      if (!audioUnlocked.current) {
        audioUnlocked.current = true;
        // Create and immediately pause to unlock
        const audio = new Audio();
        audio.play().catch(() => {});
      }
    };
    document.addEventListener('click', unlock, { once: true });
    return () => document.removeEventListener('click', unlock);
  }, []);

  useEffect(() => {
    // Inject bell animation CSS
    const style = document.createElement('style');
    style.innerHTML = bellShakeStyle;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
  setupRealtimeOrders();
  // eslint-disable-next-line
}, []);

  const playNotificationSound = () => {
    try {
      // Use Web Audio API — works without user interaction restriction
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContext();

      // Create a pleasant ding sound
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.3);

      gainNode.gain.setValueAtTime(0.5, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.5);

      // Second ding
      const oscillator2 = ctx.createOscillator();
      const gainNode2 = ctx.createGain();
      oscillator2.connect(gainNode2);
      gainNode2.connect(ctx.destination);

      oscillator2.type = 'sine';
      oscillator2.frequency.setValueAtTime(1100, ctx.currentTime + 0.3);
      oscillator2.frequency.exponentialRampToValueAtTime(550, ctx.currentTime + 0.6);

      gainNode2.gain.setValueAtTime(0.5, ctx.currentTime + 0.3);
      gainNode2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);

      oscillator2.start(ctx.currentTime + 0.3);
      oscillator2.stop(ctx.currentTime + 0.8);

    } catch (e) {
      console.log('Audio error:', e);
    }
  };

  const shakeBell = () => {
    setBellShaking(true);
    setTimeout(() => setBellShaking(false), 800);
  };

  const setupRealtimeOrders = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) return;

      const { data: rest } = await supabase
        .from('restaurants')
        .select('id, name')
        .eq('email', currentUser.email)
        .single();

      if (!rest) return;

      const channel = supabase
        .channel('orders-realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'orders',
            filter: `restaurant_id=eq.${rest.id}`
          },
          (payload) => {
            const newOrder = payload.new;

            const notification = {
              id: newOrder.id,
              title: '🛎️ New Order!',
              message: `${newOrder.customer_phone} — SAR ${newOrder.total}`,
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              read: false,
            };

            setNotifications(prev => [notification, ...prev].slice(0, 20));
            setUnreadCount(prev => prev + 1);

            // Shake bell
            shakeBell();

            // Play sound using Web Audio API
            playNotificationSound();

            // Browser notification
            if (Notification.permission === 'granted') {
              new Notification('🛎️ New Order on TabBot!', {
                body: `New order from ${newOrder.customer_phone} — SAR ${newOrder.total}`,
              });
            } else if (Notification.permission !== 'denied') {
              Notification.requestPermission();
            }
          }
        )
        .subscribe((status) => {
          console.log('Realtime status:', status);
        });

      return () => supabase.removeChannel(channel);
    } catch (err) {
      console.error('Realtime setup error:', err);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">

      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 flex flex-col transition-all duration-300`}>

        {/* Logo */}
        <div className="flex items-center justify-between px-4 py-5 border-b border-gray-200">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">T</span>
              </div>
              <span className="font-bold text-gray-800 text-lg">TabBot</span>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200
                ${isActive
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <Icon size={20} className="shrink-0" />
              {sidebarOpen && <span className="font-medium text-sm">{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Restaurant Info + Logout */}
        <div className="px-3 py-4 border-t border-gray-200">
          {sidebarOpen ? (
            <>
              <div className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-100 cursor-pointer">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-primary font-bold text-sm">{userInitial}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">My Restaurant</p>
                  <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={handleSignOut}
                className="w-full mt-2 flex items-center justify-center gap-2 text-xs text-red-400 hover:text-red-600 py-2 hover:bg-red-50 rounded-xl transition"
              >
                <LogOut size={14} />
                Sign out
              </button>
            </>
          ) : (
            <button
              onClick={handleSignOut}
              className="w-full flex items-center justify-center p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition"
            >
              <LogOut size={18} />
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Welcome back 👋</h1>
            <p className="text-sm text-gray-500">Here's what's happening today</p>
          </div>
          <div className="flex items-center gap-3">

            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  markAllRead();
                }}
                className="relative p-2 rounded-xl hover:bg-gray-100 text-gray-500"
              >
                <Bell
                  size={20}
                  className={bellShaking ? 'bell-shake text-primary' : ''}
                />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold animate-bounce">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-50">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <h3 className="font-bold text-gray-800">Notifications</h3>
                    <button
                      onClick={() => setShowNotifications(false)}
                      className="p-1 hover:bg-gray-100 rounded-lg"
                    >
                      <X size={16} className="text-gray-400" />
                    </button>
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell size={24} className="text-gray-200 mx-auto mb-2" />
                        <p className="text-gray-400 text-sm">No notifications yet</p>
                        <p className="text-gray-300 text-xs mt-1">New orders will appear here</p>
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <div
                          key={notif.id}
                          className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition ${!notif.read ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                        >
                          <p className="font-medium text-gray-800 text-sm">{notif.title}</p>
                          <p className="text-gray-500 text-xs mt-0.5">{notif.message}</p>
                          <p className="text-gray-400 text-xs mt-1">{notif.time}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-sm">{userInitial}</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}